import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import API from "../../../services/apiHelpers";
import useCftChickenBroilerValues, {
  initialStore,
} from "../../utils/useCftChickenBroilerValues";
import { CftTank } from "..";
import CftChickenBroilerReportChart from "./CftChickenBroilerReportChart";
import routes from "../../utils/cftRoutes";

function CftChickenBroilerReport() {
  const token = Cookies.get("user_token");
  const { t } = useTranslation(["cftChickenBroiler", "cftPigs"]);
  const { store, updateStore } = useCftChickenBroilerValues();
  const [report, setReport] = useState(store.report);
  const [isFetching, setFetching] = useState(false);
  const [error, setError] = useState(false);
  const numberFormat = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const history = useHistory();

  const getReport = async () => {
    const henhouse = {
      postal_code: store.main.postal_code,
      name: store.main.name,
      calculation_period_length: store.main.calculation_period_length,
      henhouses: store.henhouses_reference.coops,
      bedding: store.emission.bedding,
      energy_carriers: store.emission.energy_carriers.map(
        ({ carrier_type, value, unit }) => ({
          carrier_type,
          quantity: value,
          unit_id: unit,
        })
      ),
      transport: store.emission.transport,
    };
    setFetching(true);
    try {
      const { data } = await API().post(
        "/api/v1/ae/chicken/calculation/",
        henhouse,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setReport(data);
      updateStore({
        report: {
          ...data,
          isValidate: true,
        },
      });
    } catch (error) {
      setError(error);
    }

    setFetching(false);
  };

  useEffect(async () => {
    getReport();
  }, []);

  if (isFetching) {
    return (
      <>
        {t(
          "cftChickenBroiler:cftChickenBroilerReport.loading",
          " Generowanie raportu...."
        )}
      </>
    );
  }

  if (error) {
    return (
      <div id="report">
        <div className="cft-wrapper cft-henhouses-report">
          <CftTank
            width="100%"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            alignItems="center"
          >
            <h2 className="cft-section-title mb-3">
              {t(
                "cftChickenBroiler:cftChickenBroilerReport.error",
                "Nie można wygenerować raportu, sprawdź czy wszystkie dane zostały wprowadzone. Jeżeli błąd się powtórzy spróbój poźniej lub skontaktuj się z pomocą techniczną."
              )}
            </h2>
          </CftTank>
        </div>
      </div>
    );
  }

  return (
    <div id="report">
      <div className="cft-wrapper cft-henhouses-report">
        <CftTank
          width="100%"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="center"
        >
          <h2 className="cft-section-title mb-3">
            {t("cftChickenBroiler:cftChickenBroilerReport.title", "Raport")}
          </h2>
        </CftTank>
        <div className="cft-paper mb-4">
          <p className="cft-henhouses-report__title">
            {t(
              "cftChickenBroiler:cftChickenBroilerReport.report__title",
              "Informacje o produkcji"
            )}
          </p>
          <hr />
          <p className="cft-henhouses-report__subtitle">
            {t(
              "cftChickenBroiler:cftChickenBroilerReport.name",
              "Nazwa hodowli:"
            )}{" "}
            <span>{report.name}</span>
          </p>
          <hr />
          <p className="cft-henhouses-report__subtitle">
            {t(
              "cftChickenBroiler:cftChickenBroilerReport.postal_code",
              "Lokalizacja:"
            )}{" "}
            <span>{report.postal_code}</span>
          </p>
          <hr />
          <p className="cft-henhouses-report__subtitle">
            {t(
              "cftChickenBroiler:cftChickenBroilerReport.calculation_period_length",
              "Długość okresu obliczeniowego:"
            )}{" "}
            <span>{report.calculation_period_length}</span>
          </p>
          <hr />
          <p className="cft-henhouses-report__subtitle">
            {t(
              "cftChickenBroiler:cftChickenBroilerReport.total_carbon_footprint",
              "Całkowity ślad węglowy w analizowanym okresie"
            )}
          </p>
          <p className="cft-report__summary__value mb-4">
            {numberFormat.format(report.total_emission.carbon_footprint)}
            <small>
              {t(
                "cftPigs:cftChickenBroilerReport.total_carbon_footprintLabel",
                "kg eCO2 / {{days}} dni",
                { days: report.calculation_period_length }
              )}
            </small>
          </p>
          <CftChickenBroilerReportChart report={report} />
          <div className="cft-table-wrapper">
            <table className="cft-table cft-table--alignRight">
              <thead>
                <tr>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.header1",
                      "Środki produkcji"
                    )}
                  </th>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.header2",
                      "kg eCO2/kilogram produktu"
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.tabel.total_carrier_use",
                      "Nośniki energii"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(report.total_emission.carrier_use)}
                  </td>
                </tr>
                <tr>
                  <td>
                    {" "}
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_bedding_use",
                      "Produkcja Ściółki"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(report.total_emission.bedding_use)}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_feed_production",
                      "Paszy"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(report.total_emission.feed_production)}
                  </td>
                </tr>
                {/* <tr>
                <td>
                  {t(
                    "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement_pickup_one",
                    "Odchody - podbiórka 1"
                  )}
                </td>
                <td>
                  {numberFormat.format(report.total_excrement_pickup_one)}
                </td>
              </tr> */}
                {/* {!!report.total_excrement_pickup_two && (
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement_pickup_two",
                      "odchody - podbiórka 2"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(report.total_excrement_pickup_two)}
                  </td>
                </tr>
              )} */}
                {!!report.total_emission.excrement && (
                  <tr>
                    <td>
                      {t(
                        "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement",
                        "Związane z odchodami"
                      )}
                    </td>
                    <td>
                      {numberFormat.format(report.total_emission.excrement)}
                    </td>
                  </tr>
                )}

                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_transport_use",
                      "Transport"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(report.total_emission.transport_use)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="cft-paper mb-4">
          <p className="cft-henhouses-report__subtitle mb-4">
            {t(
              "cftChickenBroiler:cftChickenBroilerReportPerAnimal.subtitle2",
              "Całkowity ślad węglowy/ jedno zwierzę"
            )}
          </p>
          <p className="cft-report__summary__value mb-4">
            {numberFormat.format(
              report.total_emission_per_animal.carbon_footprint
            )}
            <small>
              {t(
                "cftPigs:cftChickenBroilerReport.total_carbon_footprintLabel",
                "kg eCO2 / {{days}} dni",
                { days: report.calculation_period_length }
              )}
            </small>
          </p>
          <div className="cft-table-wrapper">
            <table className="cft-table cft-table--alignRight">
              <thead>
                <tr>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.header1",
                      "Środki produkcji"
                    )}
                  </th>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.header2",
                      "kg eCO2/kilogram produktu"
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.tabel.total_carrier_use",
                      "Nośniki energii"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_animal.carrier_use
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    {" "}
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_bedding_use",
                      "Produkcja Ściółki"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_animal.bedding_use
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_feed_production",
                      "Paszy"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_animal.feed_production
                    )}
                  </td>
                </tr>
                {/* <tr>
                <td>
                  {t(
                    "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement_pickup_one",
                    "Odchody - podbiórka 1"
                  )}
                </td>
                <td>
                  {numberFormat.format(report.total_excrement_pickup_one)}
                </td>
              </tr> */}
                {/* {!!report.total_excrement_pickup_two && (
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement_pickup_two",
                      "odchody - podbiórka 2"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(report.total_excrement_pickup_two)}
                  </td>
                </tr>
              )} */}
                {!!report.total_emission_per_animal.excrement && (
                  <tr>
                    <td>
                      {t(
                        "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement",
                        "Związane z odchodami"
                      )}
                    </td>
                    <td>
                      {numberFormat.format(
                        report.total_emission_per_animal.excrement
                      )}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_transport_use",
                      "Transport"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_animal.transport_use
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="cft-paper mb-4">
          <p className="cft-henhouses-report__subtitle mb-4">
            {t(
              "cftChickenBroiler:cftChickenBroilerReportPerKg.subtitle2",
              "Całkowity ślad węglowy/ kilogram produktu"
            )}
          </p>
          <p className="cft-report__summary__value mb-4">
            {numberFormat.format(
              report.total_emission_per_kilogram_of_product.carbon_footprint
            )}
            <small>
              {t(
                "cftPigs:cftChickenBroilerReport.total_carbon_footprintLabel",
                "kg eCO2 / {{days}} dni",
                { days: report.calculation_period_length }
              )}
            </small>
          </p>
          <div className="cft-table-wrapper">
            <table className="cft-table cft-table--alignRight">
              <thead>
                <tr>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.header1",
                      "Środki produkcji"
                    )}
                  </th>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.header2",
                      "kg eCO2/kilogram produktu"
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.tabel.total_carrier_use",
                      "Nośniki energii"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_kilogram_of_product.carrier_use
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    {" "}
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_bedding_use",
                      "Produkcja Ściółki"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_kilogram_of_product.bedding_use
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_feed_production",
                      "Paszy"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_kilogram_of_product
                        .feed_production
                    )}
                  </td>
                </tr>
                {/* <tr>
                <td>
                  {t(
                    "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement_pickup_one",
                    "Odchody - podbiórka 1"
                  )}
                </td>
                <td>
                  {numberFormat.format(report.total_excrement_pickup_one)}
                </td>
              </tr> */}
                {/* {!!report.total_excrement_pickup_two && (
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement_pickup_two",
                      "odchody - podbiórka 2"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(report.total_excrement_pickup_two)}
                  </td>
                </tr>
              )} */}
                {!!report.total_emission_per_kilogram_of_product.excrement && (
                  <tr>
                    <td>
                      {t(
                        "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement",
                        "Związane z odchodami"
                      )}
                    </td>
                    <td>
                      {numberFormat.format(
                        report.total_emission_per_kilogram_of_product.excrement
                      )}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_transport_use",
                      "Transport"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_kilogram_of_product
                        .transport_use
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="cft-paper mb-4">
          <p className="cft-henhouses-report__subtitle mb-4">
            {t(
              "cftChickenBroiler:cftChickenBroilerReport.subtitle2",
              "Całkowity ślad węglowy/ Kurnik referencyjny"
            )}
          </p>
          <div className="cft-table-wrapper">
            <table className="cft-table cft-table--alignRight">
              <thead>
                <tr>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.henhouses.header1",
                      "Nazwa kurnika"
                    )}
                  </th>
                  {report.housing.map(({ name }) => (
                    <th key={name}>{name}</th>
                  ))}
                </tr>
                <tr>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.henhouses.header2",
                      "Środki produkcji"
                    )}
                  </th>
                  {report.housing.map(({ name }) => (
                    <th key={name}>kg eCO2</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.carbon_footprint",
                      "Całkowity ślad węglowy w kurniku referencyjnym"
                    )}
                  </td>
                  {report.housing.map(({ carbon_footprint }, key) => (
                    <td key={`carbon_footprint_${key}`}>
                      {numberFormat.format(carbon_footprint)}
                    </td>
                  ))}
                </tr>
                {/* <tr>
                <td>
                  {t(
                    "cftChickenBroiler:cftChickenBroilerReport.table.carrier_use",
                    " Nośniki energii"
                  )}
                </td>
                {report.henhouses.map(({ carrier_use }, key) => (
                  <td key={`carrier_use_${key}`}>
                    {numberFormat.format(carrier_use)}
                  </td>
                ))}
              </tr> */}
                {/* <tr>
                <td>
                  {t(
                    "cftChickenBroiler:cftChickenBroilerReport.table.bedding_use",
                    "Produkcja Ściółki"
                  )}
                </td>
                {report.henhouses.map(({ bedding_use }, key) => (
                  <td key={`total_bedding_use_${key}`}>
                    {numberFormat.format(bedding_use)}
                  </td>
                ))}
              </tr> */}
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.feed_production",
                      "Paszy"
                    )}
                  </td>
                  {report.housing.map(({ feed_production }, key) => (
                    <td key={`feed_production_${key}`}>
                      {numberFormat.format(feed_production)}
                    </td>
                  ))}
                </tr>
                {/* <tr>
                <td>
                  {t(
                    "cftChickenBroiler:cftChickenBroilerReport.table.excrement_pickup_one",
                    "Odchody - podbiórka 1"
                  )}
                </td>
                {report.henhouses.map(({ excrement_pickup_one }, key) => (
                  <td key={`excrement_pickup_one_${key}`}>
                    {numberFormat.format(excrement_pickup_one)}
                  </td>
                ))}
              </tr> */}
                {/* <tr>
                <td>
                  {t(
                    "cftChickenBroiler:cftChickenBroilerReport.table.excrement_pickup_two",
                    "odchody - podbiórka 2"
                  )}
                </td>
                {report.henhouses.map(({ excrement_pickup_two }, key) => (
                  <td key={`excrement_pickup_two_${key}`}>
                    {numberFormat.format(excrement_pickup_two)}
                  </td>
                ))}
              </tr> */}
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.excrement",
                      "Związane z odchodami"
                    )}
                  </td>
                  {report.housing.map(({ excrement }, key) => (
                    <td key={`excrement_${key}`}>
                      {numberFormat.format(excrement)}
                    </td>
                  ))}
                </tr>
                {/* <tr>
                <td>
                  {t(
                    "cftChickenBroiler:cftChickenBroilerReport.table.transport_use",
                    "Transport"
                  )}
                </td>
                {report.henhouses.map(({ transport_use }, key) => (
                  <td key={`transport_use_${key}`}>
                    {numberFormat.format(transport_use)}
                  </td>
                ))}
              </tr> */}
              </tbody>
            </table>
          </div>
        </div>
        <div className="cft-paper mb-4">
          <p className="cft-henhouses-report__subtitle mb-4">
            {t(
              "cftChickenBroiler:cftChickenBroilerReportPerProductionCycle.subtitle2",
              "Ślad węglowy/ cykl produkcyjny"
            )}
          </p>
          <p className="cft-report__summary__value mb-4">
            {numberFormat.format(
              report.total_emission_per_production_cycle.carbon_footprint
            )}
            <small>
              {t(
                "cftPigs:cftChickenBroilerReport.total_carbon_footprintLabel",
                "kg eCO2 / {{days}} dni",
                { days: report.calculation_period_length }
              )}
            </small>
          </p>
          <div className="cft-table-wrapper">
            <table className="cft-table cft-table--alignRight">
              <thead>
                <tr>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.header1",
                      "Środki produkcji"
                    )}
                  </th>
                  <th>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.header2",
                      "kg eCO2/kilogram produktu"
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.tabel.total_carrier_use",
                      "Nośniki energii"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_production_cycle.carrier_use
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    {" "}
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_bedding_use",
                      "Produkcja Ściółki"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_production_cycle.bedding_use
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_feed_production",
                      "Paszy"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_production_cycle.feed_production
                    )}
                  </td>
                </tr>
                {/* <tr>
                <td>
                  {t(
                    "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement_pickup_one",
                    "Odchody - podbiórka 1"
                  )}
                </td>
                <td>
                  {numberFormat.format(report.total_excrement_pickup_one)}
                </td>
              </tr> */}
                {/* {!!report.total_excrement_pickup_two && (
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement_pickup_two",
                      "odchody - podbiórka 2"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(report.total_excrement_pickup_two)}
                  </td>
                </tr>
              )} */}
                {!!report.total_emission_per_production_cycle.excrement && (
                  <tr>
                    <td>
                      {t(
                        "cftChickenBroiler:cftChickenBroilerReport.table.total_excrement",
                        "Związane z odchodami"
                      )}
                    </td>
                    <td>
                      {numberFormat.format(
                        report.total_emission_per_production_cycle.excrement
                      )}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>
                    {t(
                      "cftChickenBroiler:cftChickenBroilerReport.table.total_transport_use",
                      "Transport"
                    )}
                  </td>
                  <td>
                    {numberFormat.format(
                      report.total_emission_per_production_cycle.transport_use
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <CftTank display="flex" flexDirection="column" alignItems="center">
          <button
            className="hide-on-pdf"
            onClick={() => {
              updateStore(initialStore);
              setTimeout(() => {
                history.push(routes.CHICKEN_BROILER.MAIN);
              }, 100);
            }}
            style={{
              margin: "0 1rem",
              background: "transparent",
              border: "none",
            }}
            type="button"
          >
            {t("cftChickenBroiler:clear", "Wyczyść kalkulację")}
          </button>
        </CftTank>
        <CftTank display="flex" flexDirection="column" alignItems="center">
          <p
            style={{
              fontSize: "0.75rem",
              textAlign: "center",
              marginTop: "1rem",
            }}
          >
            {t("cftChickenBroiler:disclaimera")}
          </p>
        </CftTank>
      </div>
    </div>
  );
}

export default CftChickenBroilerReport;
